import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Avatar, Chip, Button,
  Skeleton, Alert, LinearProgress, Tooltip, Divider,
} from '@mui/material';
import {
  School, Star, Person, CalendarMonth, ContentCopy,
  LinkedIn, Language, ArrowBack, TrendingUp,
} from '@mui/icons-material';
import knowledgeService from '../../services/knowledgeService';
import { colors, designTokens } from '../../theme/theme';

function BadgeChip({ badge }: { badge: { emoji: string; title: string; description: string } }) {
  return (
    <Tooltip title={badge.description} arrow>
      <Chip
        label={`${badge.emoji} ${badge.title}`}
        size="small"
        sx={{
          bgcolor: colors.primary.subtle,
          color: colors.primary.dark,
          fontWeight: 600,
          border: `1px solid ${colors.primary.light}`,
        }}
      />
    </Tooltip>
  );
}

function RatingBar({ label, value, max = 5 }: { label: string; value: number | null; max?: number }) {
  if (!value) return null;
  const pct = (value / max) * 100;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value.toFixed(1)}/5</Typography>
      </Box>
      <LinearProgress
        variant="determinate" value={pct}
        sx={{
          height: 6, borderRadius: 3,
          bgcolor: colors.neutral[100],
          '& .MuiLinearProgress-bar': { bgcolor: pct >= 90 ? colors.success.main : pct >= 70 ? colors.primary.main : colors.warning.main },
        }}
      />
    </Box>
  );
}

export default function ProfessionalProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!userId) return;
    knowledgeService.getProfessionalProfile(userId)
      .then(setProfile)
      .catch(e => setError(e.message || 'Profile not found'))
      .finally(() => setLoading(false));
  }, [userId]);

  const shareUrl = `${window.location.origin}/knowledge/pro/${userId}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Skeleton height={200} sx={{ borderRadius: 3 }} />
      <Skeleton height={300} sx={{ borderRadius: 3, mt: 2 }} />
    </Container>
  );

  if (error || !profile) return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Alert severity="error">{error || 'Professional not found'}</Alert>
      <Button onClick={() => navigate('/knowledge')} sx={{ mt: 2 }}>Browse Sessions</Button>
    </Container>
  );

  const p = profile;
  const fullName = `${p.first_name} ${p.last_name}`;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>

      {/* Hero Card */}
      <Paper
        elevation={0}
        sx={{
          p: 4, mb: 3, borderRadius: 4,
          bgcolor: designTokens.glass.surface,
          border: `1px solid ${designTokens.glass.border}`,
          backdropFilter: designTokens.glass.blur,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Avatar
            src={p.avatar_url || undefined}
            sx={{ width: 100, height: 100, bgcolor: colors.primary.main, fontSize: 36, border: `3px solid ${colors.primary.light}` }}
          >
            {p.first_name[0]}{p.last_name[0]}
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>{fullName}</Typography>

            {p.industry && (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>{p.industry}</Typography>
            )}

            {/* Badges */}
            {p.badges && p.badges.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                {p.badges.map((b: any) => <BadgeChip key={b.id} badge={b} />)}
              </Box>
            )}

            {/* Level */}
            {p.level && (
              <Chip label={p.level.charAt(0).toUpperCase() + p.level.slice(1)} size="small"
                sx={{ bgcolor: colors.neutral[100], fontWeight: 600, mr: 1 }} />
            )}

            {/* Bio */}
            {(p.detailed_bio || p.bio) && (
              <Typography variant="body1" sx={{ mt: 1.5, lineHeight: 1.7 }}>
                {p.detailed_bio || p.bio}
              </Typography>
            )}

            {/* Links */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              {p.linkedin_url && (
                <Button size="small" startIcon={<LinkedIn />} href={p.linkedin_url} target="_blank" rel="noopener">
                  LinkedIn
                </Button>
              )}
              {p.portfolio_url && (
                <Button size="small" startIcon={<Language />} href={p.portfolio_url} target="_blank" rel="noopener">
                  Portfolio
                </Button>
              )}
              <Button size="small" startIcon={copied ? null : <ContentCopy />} onClick={handleCopyLink}>
                {copied ? 'Copied!' : 'Share Profile'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Stats Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { icon: <School />, value: p.sessions_taught, label: 'Sessions' },
          { icon: <Person />, value: p.total_students_taught || 0, label: 'Students' },
          { icon: <Star sx={{ color: '#F2994A' }} />, value: p.avg_teaching_rating?.toFixed(1) || 'N/A', label: 'Avg Rating' },
          { icon: <CalendarMonth />, value: p.professional_since ? new Date(p.professional_since).toLocaleDateString('en', { month: 'short', year: 'numeric' }) : 'New', label: 'Teaching Since' },
        ].map((stat, i) => (
          <Paper key={i} sx={{ p: 2, flex: '1 1 120px', textAlign: 'center', borderRadius: 3 }}>
            {stat.icon}
            <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>{stat.value}</Typography>
            <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
          </Paper>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {/* Left: Ratings + Topics */}
        <Box sx={{ flex: '1 1 300px' }}>
          {/* Rating Breakdown */}
          {p.rating_breakdown?.total_ratings > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Rating Breakdown
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({p.rating_breakdown.total_ratings} reviews)
                </Typography>
              </Typography>
              <RatingBar label="Knowledge Quality" value={p.rating_breakdown.knowledge_quality} />
              <RatingBar label="Communication" value={p.rating_breakdown.communication} />
              <RatingBar label="Value for Money" value={p.rating_breakdown.value_for_money} />
              <Divider sx={{ my: 1.5 }} />
              <RatingBar label="Overall" value={p.rating_breakdown.overall} />
            </Paper>
          )}

          {/* Topic Expertise */}
          {p.topic_expertise && p.topic_expertise.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Topic Expertise</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {p.topic_expertise.map((t: string) => (
                  <Chip key={t} label={t} sx={{ bgcolor: colors.neutral[100], fontWeight: 500 }} />
                ))}
              </Box>
            </Paper>
          )}

          {/* Interests */}
          {p.interests && p.interests.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Interests</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {p.interests.map((t: string) => (
                  <Chip key={t} label={t} size="small" variant="outlined" />
                ))}
              </Box>
            </Paper>
          )}
        </Box>

        {/* Right: Sessions */}
        <Box sx={{ flex: '1 1 300px' }}>
          {/* Upcoming Sessions */}
          {p.upcoming_sessions && p.upcoming_sessions.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                Upcoming Sessions
              </Typography>
              {p.upcoming_sessions.map((s: any) => (
                <Paper key={s.id} variant="outlined" sx={{
                  p: 2, mb: 1.5, borderRadius: 2, cursor: 'pointer',
                  '&:hover': { borderColor: colors.primary.main },
                }} onClick={() => navigate(`/events/${s.id}`)}>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{s.title}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{s.date} at {s.time}</Typography>
                    <Chip label={`${s.spots_left} spot${s.spots_left !== 1 ? 's' : ''} left`} size="small"
                      color={s.spots_left > 0 ? 'success' : 'error'} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary.main, mt: 0.5 }}>
                    {s.total_price} AZN
                  </Typography>
                </Paper>
              ))}
            </Paper>
          )}

          {/* Past Sessions */}
          {p.past_sessions && p.past_sessions.length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Past Sessions</Typography>
              {p.past_sessions.slice(0, 10).map((s: any) => (
                <Box key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: colors.neutral[100] }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.date} · {s.students} student{s.students !== 1 ? 's' : ''}</Typography>
                  </Box>
                  {s.rating && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <Star sx={{ fontSize: 14, color: '#F2994A' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.rating.toFixed(1)}</Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Paper>
          )}
        </Box>
      </Box>
    </Container>
  );
}
